// Global Instructions Rule Applied!

import { TableFilterOptionsResponse } from '../../../state_manager/rpc_models/get_table_filter_options_response.ts'
import {
  ContactsPaginatedResponse,
  fetchContactsFilterOptions,
  fetchContactsPaginated,
  fetchContactsFiltered
} from '../services/contactsService.ts'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useSelector } from 'react-redux'
import {
  setContacts,
  setFilterOptions,
  setActiveFilters,
  setActiveSorting,
  clearActiveFilters,
  appendContacts
} from '../../../state_manager/state_update_actions/contacts/index.ts'
import { FilterValue, SortConfig } from '../../../state_manager/store_models/contacts.ts'
import type { AppDispatch } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { useMenuItemUserPreference } from '../../global_configs/useMenuItemUserPreference.ts'
import {
  mergeUserPreferenceRowData,
  removeUserPreferenceForMenuItem
} from '../../../state_manager/state_update_actions/global_configs/actions.ts'
import {
  areFilterValuesEqual,
  DEFAULT_CONTACTS_TABLE_FILTERS,
  areSortConfigsEqual,
  PREF_TABLE_COLUMN_KEYS,
  PREF_TABLE_FILTERS,
  PREF_TABLE_SORTING,
  resolveTableFiltersFromPreferenceRow,
  resolveTableSortingFromPreferenceRow
} from '../../global_configs/tableViewPreferences.ts'
import { useReportingDefaultSorting } from '../../global_configs/useReportingDefaultSorting.ts'
import { MENU_ITEM_KEY_CONTACTS } from '../../../state_manager/globals.ts'
import { fetchGlobalConfigsThunk } from '../../global_configs/controller.ts'
import { useContactsReportId } from '../../../state_manager/hooks/useContactsReportId.ts'

export const useContactsData = () => {
  const dispatch = useDispatch<AppDispatch>()

  const { menuItemId, preferenceRow, persistPreference, deletePreference } = useMenuItemUserPreference(
    MENU_ITEM_KEY_CONTACTS
  )

  const contactsReportId = useContactsReportId()
  const defaultTableSorting = useReportingDefaultSorting(contactsReportId)

  const [contactsDataLoading, setContactsDataLoading] = useState(false)
  const [contactsFilterOptionsLoading, setContactsFilterOptionsLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [prefsReady, setPrefsReady] = useState(false)
  const skipPersistRef = useRef(true)

  const contactsData = useSelector((state: any) => state.contacts.data)
  const contactsFilterOptions = useSelector((state: any) => state.contacts.filterOptions)
  const activeFilters = useSelector((state: any) => state.contacts.activeFilters)
  const activeSorting = useSelector((state: any) => state.contacts.activeSorting)
  const hasMore = useSelector((state: any) => state.contacts.hasMore ?? false)
  const currentPage = useSelector((state: any) => state.contacts.currentPage ?? 1)

  const menuItems = useSelector((state: any) => state.globalConfigs?.menu_items ?? [])

  useEffect(() => {
    if (menuItems.length === 0) return
    if (!menuItemId) {
      dispatch(setActiveFilters({ filters: DEFAULT_CONTACTS_TABLE_FILTERS }))
      dispatch(setActiveSorting({ sorting: defaultTableSorting }))
      setPrefsReady(true)
      return
    }
    const filters = resolveTableFiltersFromPreferenceRow(preferenceRow, DEFAULT_CONTACTS_TABLE_FILTERS)
    const sorting = resolveTableSortingFromPreferenceRow(preferenceRow, defaultTableSorting)
    if (!areFilterValuesEqual(filters, activeFilters)) {
      dispatch(setActiveFilters({ filters }))
    }
    if (!areSortConfigsEqual(sorting, activeSorting)) {
      dispatch(setActiveSorting({ sorting }))
    }
    setPrefsReady(true)
  }, [menuItemId, menuItems.length, preferenceRow, defaultTableSorting, dispatch])

  const handleFetchFilterOptions = useCallback(async () => {
    try {
      setContactsFilterOptionsLoading(true)
      const currentToken = getAccessToken(store.getState().auth)
      const response: TableFilterOptionsResponse = await fetchContactsFilterOptions(currentToken)
      dispatch(setFilterOptions({ response }))
    } catch (e) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching filter options:', e)
      }
    } finally {
      setContactsFilterOptionsLoading(false)
    }
  }, [dispatch])

  const handleFetchContacts = useCallback(
    async (sorting: SortConfig[] = [], filters?: FilterValue[]) => {
      try {
        setContactsDataLoading(true)
        const currentToken = getAccessToken(store.getState().auth)
        const hasFilters = filters && filters.length > 0
        const hasSorting = sorting.length > 0

        const response: ContactsPaginatedResponse =
          hasFilters || hasSorting
            ? await fetchContactsFiltered(sorting, filters || [], 1, null, currentToken, contactsReportId)
            : await fetchContactsPaginated(1, null, currentToken, contactsReportId)

        dispatch(setContacts({ response }))
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error fetching contacts:', e)
        }
      } finally {
        setContactsDataLoading(false)
      }
    },
    [dispatch, contactsReportId]
  )

  const handleSetFilters = useCallback(
    (filters: FilterValue[]) => {
      if (areFilterValuesEqual(filters, activeFilters)) return
      dispatch(setActiveFilters({ filters }))
    },
    [dispatch, activeFilters]
  )

  const handleSetSorting = useCallback(
    (sorting: SortConfig[]) => {
      if (areSortConfigsEqual(sorting, activeSorting)) return
      dispatch(setActiveSorting({ sorting }))
    },
    [dispatch, activeSorting]
  )

  const handleClearFilters = useCallback(() => {
    dispatch(clearActiveFilters())
  }, [dispatch])

  const handleResetFilters = useCallback(() => {
    if (!menuItemId) {
      dispatch(setActiveFilters({ filters: DEFAULT_CONTACTS_TABLE_FILTERS }))
      return
    }
    void (async () => {
      const result = await deletePreference()
      if (!result.success) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Failed to delete contacts table preferences:', result.error)
        }
        return
      }
      dispatch(removeUserPreferenceForMenuItem(menuItemId))
      skipPersistRef.current = true
      dispatch(setActiveFilters({ filters: DEFAULT_CONTACTS_TABLE_FILTERS }))
      dispatch(setActiveSorting({ sorting: defaultTableSorting }))
    })()
  }, [dispatch, menuItemId, deletePreference, defaultTableSorting])

  const handleClearSorting = useCallback(() => {
    dispatch(setActiveSorting({ sorting: defaultTableSorting }))
  }, [dispatch, defaultTableSorting])

  const persistMergedPreference = useCallback(
    async (extra: Record<string, unknown> = {}) => {
      if (!menuItemId) return { success: false as const, error: 'No menu item' }
      const reports = store.getState().globalConfigs?.user_preferences_reports ?? []
      const hadExistingRow = reports.some((p) => p.menu_item_id === menuItemId)
      const row = reports.find((p) => p.menu_item_id === menuItemId)
      const merged: Record<string, unknown> = {
        ...(row?.data ?? {}),
        ...extra,
        [PREF_TABLE_FILTERS]: activeFilters,
        [PREF_TABLE_SORTING]: activeSorting
      }
      const result = await persistPreference(undefined, merged)
      if (result.success) {
        if (hadExistingRow) {
          dispatch(mergeUserPreferenceRowData(menuItemId, merged))
        } else {
          const user = store.getState().auth.user
          if (user) {
            await fetchGlobalConfigsThunk(user)(dispatch)
          }
        }
      }
      return result
    },
    [menuItemId, activeFilters, activeSorting, persistPreference, dispatch]
  )

  const persistColumnFieldOrder = useCallback(
    async (fieldNames: string[]) => {
      return persistMergedPreference({ [PREF_TABLE_COLUMN_KEYS]: fieldNames })
    },
    [persistMergedPreference]
  )

  const resetColumnLayoutToDefault = useCallback(async () => {
    if (!menuItemId) return { success: false as const, error: 'No menu item' }
    const reports = store.getState().globalConfigs?.user_preferences_reports ?? []
    const hadExistingRow = reports.some((p) => p.menu_item_id === menuItemId)
    const row = reports.find((p) => p.menu_item_id === menuItemId)
    const base: Record<string, unknown> = { ...(row?.data ?? {}) }
    delete base[PREF_TABLE_COLUMN_KEYS]
    const merged: Record<string, unknown> = {
      ...base,
      [PREF_TABLE_FILTERS]: activeFilters,
      [PREF_TABLE_SORTING]: activeSorting
    }
    const result = await persistPreference(undefined, merged)
    if (result.success) {
      if (hadExistingRow) {
        dispatch(mergeUserPreferenceRowData(menuItemId, merged))
      } else {
        const user = store.getState().auth.user
        if (user) {
          await fetchGlobalConfigsThunk(user)(dispatch)
        }
      }
    }
    return result
  }, [menuItemId, activeFilters, activeSorting, persistPreference, dispatch])

  const handleLoadMore = useCallback(
    async (page: number, pageSize: number) => {
      if (loadingMore || !hasMore) return

      try {
        setLoadingMore(true)
        const currentToken = getAccessToken(store.getState().auth)
        const hasFilters = activeFilters && activeFilters.length > 0
        const hasSorting = Array.isArray(activeSorting) && activeSorting.length > 0

        let response: ContactsPaginatedResponse
        if (hasFilters || hasSorting) {
          response = await fetchContactsFiltered(
            activeSorting,
            activeFilters,
            page,
            pageSize,
            currentToken,
            contactsReportId
          )
        } else {
          response = await fetchContactsPaginated(page, pageSize, currentToken, contactsReportId)
        }

        dispatch(appendContacts({ response }))
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error loading more contacts:', e)
        }
      } finally {
        setLoadingMore(false)
      }
    },
    [dispatch, activeFilters, activeSorting, hasMore, loadingMore, contactsReportId]
  )

  useEffect(() => {
    if (!prefsReady) return
    const currentToken = getAccessToken(store.getState().auth)
    if (currentToken) {
      handleFetchFilterOptions()
      handleFetchContacts(activeSorting, activeFilters)
    } else {
      setContactsFilterOptionsLoading(false)
    }
  }, [prefsReady, activeFilters, activeSorting, handleFetchFilterOptions, handleFetchContacts])

  useEffect(() => {
    if (!prefsReady || !menuItemId) return
    if (skipPersistRef.current) {
      skipPersistRef.current = false
      return
    }
    void persistMergedPreference().catch((err) => {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to persist contacts table preferences:', err)
      }
    })
  }, [prefsReady, menuItemId, activeFilters, activeSorting, persistMergedPreference])

  return {
    contactsFilterOptions: contactsFilterOptions.data,
    contactsData,
    contactsDataLoading,
    contactsFilterOptionsLoading,
    activeFilters,
    activeSorting,
    preferenceRow,
    hasMore,
    currentPage,
    loadingMore,
    handleFetchContacts,
    handleFetchFilterOptions,
    handleSetFilters,
    handleSetSorting,
    handleClearFilters,
    handleClearSorting,
    handleLoadMore,
    defaultTableFilters: DEFAULT_CONTACTS_TABLE_FILTERS,
    defaultTableSorting,
    handleResetFilters,
    persistColumnFieldOrder,
    resetColumnLayoutToDefault
  }
}
